import { IssueType } from '../defects/issue.base';

/**
 * Supported browser notification event types.
 */
export enum BrowserNtfEventType {
  DEFECT_NEW = 'defect.new',
  DEFECT_UPDATE = 'defect.update',
  ACCESS_REQUEST_MOBILE = 'access.request.mobile',
  ACCESS_REQUEST_CRM = 'access.request.crm',
  CHAT_NEW = 'chat.new',
  BOOKING_NEW = 'booking.new',
  CALENDAR_EVENT = 'calendar.event',
  DEADLINE_UPCOMING = 'deadline.upcoming',
  SYSTEM_VERSION = 'system.version',
  CONCIERGE_NEW = 'concierge.new',
  CONCIERGE_UPDATE = 'concierge.update',
  ISSUE_NEW = 'residentIssue.new',
  ISSUE_UPDATE = 'residentIssue.update',
  OFFER_RESERVATION_NEW = 'offer.reservation.new'
}

export namespace BrowserNtfEventType {
  /**
   * Builds BrowserNtfEventType for issue event
   * @param {IssueType} type
   * @param {string}    event
   * @return {BrowserNtfEventType}
   */
  export function forIssue(type: IssueType, event: 'new' | 'update'): BrowserNtfEventType {
    return `${type}.${event}` as BrowserNtfEventType;
  }
}

/**
 * Primitive value supported in browser notification event payload.
 */
export type BrowserNtfEventValue = string | number | boolean;

/**
 * Dynamic event payload map for browser notifications.
 */
export interface BrowserNtfEventData {
  [key: string]: BrowserNtfEventValue;
}

/**
 * Additional metadata attached to browser notification.
 */
export interface BrowserNtfMeta {
  foreignId?: string | null;
  source?: string | null;
}

/**
 * Browser notification object persisted and delivered to user.
 */
export interface BrowserNtfObj {
  id: string;
  userId: string;
  createDate: number;
  lastUpdate: number;
  displayed?: boolean;

  eventType?: BrowserNtfEventType | string;
  eventData?: BrowserNtfEventData | null;

  title?: string;
  body?: string;

  deepLink: string;
  estateId?: string | null;
  estateName?: string | null;
  tag?: string | null;
  requireInteraction?: boolean | null;
  playSound?: boolean | null;

  meta?: BrowserNtfMeta | null;
}

export interface BrowserNtfHistoryRequest {
  limit?: number;
  beforeCreateDate?: number;
  eventTypes?: BrowserNtfEventType[];
}

export interface BrowserNtfDisplayedRequest {
  displayed?: boolean;
  ids?: string[];
  all?: boolean;
  eventTypes?: BrowserNtfEventType[];
}

export const BROWSER_NTF_SUBSCRIPTION_KEYS = [
  'defectNew',
  'defectUpdate',
  'accessRequestMobile',
  'accessRequestCrm',
  'chatNew',
  'bookingNew',
  'calendarEvent',
  'deadlineUpcoming',
  'systemVersion',
  'concierge'
] as const;

export type BrowserNtfSubscriptionKey = (typeof BROWSER_NTF_SUBSCRIPTION_KEYS)[number];

/**
 * User browser notification preferences. LACK OF OBJECT OR FIELD MEANS TRUE.
 */
export type BrowserNtfSubscription = Partial<Record<BrowserNtfSubscriptionKey, boolean>> & {
  enabled?: boolean;
};

/**
 * Web Push subscription payload stored per user/browser.
 */
export interface WebPushSubscriptionData {
  id: string;
  userId: string;
  endpoint: string;
  expirationTime: number | null;
  keys: {
    auth: string;
    p256dh: string;
  };
  language: string;
  userAgent: string;
  createDate: number;
  lastUpdate: number;
}
